import { supabase } from "@/lib/supabase";

// Direct messages, newest conversation first — like a messenger inbox.
//
// A conversation is a PERSON (whichever of you spoke last), so the time that places
// someone in the list is the newest message between the two of you, sent or received.
// People you have never messaged keep the order they came in (alphabetical) below the
// ones you have, so a long directory never buries the people you actually talk to.

export interface DmRow { sender_id: string; recipient_id: string | null; created_at: string }

/** The newest message time (ms) between `myId` and each other person. */
export function latestByPerson(rows: DmRow[], myId: string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of rows) {
    const other = r.sender_id === myId ? r.recipient_id : r.recipient_id === myId ? r.sender_id : null;
    if (!other || other === myId) continue;
    const t = Date.parse(r.created_at);
    if (Number.isFinite(t) && t > (out[other] ?? 0)) out[other] = t;
  }
  return out;
}

/**
 * People you have talked to, newest first; then everyone else in their existing order.
 * Returns the same array when nothing has been said yet, so React sees no change.
 */
export function sortByRecent<T>(items: T[], idOf: (item: T) => string, latest: Record<string, number>): T[] {
  const talked = items.filter(i => (latest[idOf(i)] ?? 0) > 0);
  if (talked.length === 0) return items;
  const rest = items.filter(i => !((latest[idOf(i)] ?? 0) > 0));
  // Array.prototype.sort is stable, so equal times keep their incoming order.
  talked.sort((a, b) => (latest[idOf(b)] ?? 0) - (latest[idOf(a)] ?? 0));
  return [...talked, ...rest];
}

/** Record that a message just passed between me and `otherId`. */
export function bumpRecent(prev: Record<string, number>, otherId: string, at = Date.now()): Record<string, number> {
  return (prev[otherId] ?? 0) >= at ? prev : { ...prev, [otherId]: at };
}

// How far back to look. The newest thousand direct messages cover every conversation that
// is still alive; anyone older simply sits with the people you have not messaged.
const LOOKBACK = 1000;

/**
 * Loads the latest-message time for each person. Row security keeps this to my own DMs.
 * Any failure leaves the list alphabetical: ordering is a nicety and must never take the chat list down.
 */
export async function fetchDmRecents(myId: string): Promise<Record<string, number>> {
  try {
    const { data, error } = await supabase
      .from("hq_chat_messages")
      .select("sender_id,recipient_id,created_at")
      .eq("is_direct_message", true)
      .is("deleted_at", null)
      .or(`sender_id.eq.${myId},recipient_id.eq.${myId}`)
      .order("created_at", { ascending: false })
      .limit(LOOKBACK);
    if (error) return {};
    return latestByPerson((data as DmRow[]) ?? [], myId);
  } catch {
    return {};
  }
}
